import { useState } from 'react';
import { ErreurApi } from '../api/client';
import { seConnecter } from '../api/sygap';
import { Bouton } from '../composants/Bouton';
import { Champ } from '../composants/Champ';
import { Message } from '../composants/Message';
import { naviguer } from '../routage/routeur';
import { useSession } from '../session/ContexteSession';
import { accueilDe } from '../session/session';

/**
 * Écran de connexion.
 *
 * Le message d'erreur ne distingue jamais « login inconnu » de « mot de passe
 * faux » : l'API applique déjà cette règle anti-énumération, l'interface ne doit
 * pas la contredire en interprétant le code d'erreur.
 *
 * Mise en page en deux volets plutôt qu'une carte centrée. Une carte flottante
 * au milieu d'un fond uni est le gabarit de connexion le plus vu du web, et il
 * ne dit rien de qui édite le service. Ici le volet institutionnel **nomme
 * l'État** avant que l'on saisisse quoi que ce soit — c'est ce qu'un usager doit
 * reconnaître d'abord, et c'est aussi ce qui distingue un service public d'une
 * page d'hameçonnage. Sous 1024 px le volet se replie en bandeau : il informe,
 * il ne doit pas repousser le formulaire sous la ligne de flottaison.
 */
export function PageConnexion() {
  const { ouvrir } = useSession();
  const [login, setLogin] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  // Événement DOM natif : Preact ne synthétise pas les événements, contrairement
  // à React. C'est le vrai `SubmitEvent` qui arrive ici.
  async function soumettre(evenement: Event): Promise<void> {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const reponse = await seConnecter(login, motDePasse);
      const session = ouvrir(reponse.accessToken, reponse.forceChangementMdp);
      if (session === null) {
        setErreur("Le service a rendu une session illisible. Signalez-le à l'administrateur.");
        return;
      }
      // L'écran d'arrivée dépend du rôle : envoyer tout le monde sur la file
      // d'instruction ferait atterrir chaque professionnel sur le poste agent.
      naviguer(accueilDe(session), { remplacer: true });
    } catch (cause) {
      setErreur(
        cause instanceof ErreurApi
          ? cause.message
          : 'La connexion a échoué pour une raison inattendue.',
      );
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="min-h-svh lg:grid lg:grid-cols-[1fr_30rem] xl:grid-cols-[1fr_34rem]">
      <VoletInstitutionnel />

      <main
        id="contenu"
        className="flex items-start justify-center bg-background px-6 py-10 lg:items-center lg:px-12 lg:py-16"
      >
        <div className="w-full max-w-sm">
          <h2 className="text-2xl font-bold tracking-tight text-foreground">Connexion</h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Réservée aux comptes ouverts par une administration du circuit.
          </p>

          <form
            onSubmit={(evenement) => {
              void soumettre(evenement);
            }}
            noValidate
            className="mt-7 flex flex-col gap-5"
          >
            {/* L'erreur est au-dessus des champs et porte `role="alert"` : elle
                doit être annoncée sans que l'usager ait à la chercher. */}
            {erreur !== null && <Message ton="erreur" titre={erreur} />}

            <Champ
              libelle="Identifiant"
              name="login"
              autoComplete="username"
              required
              value={login}
              onChange={(evenement) => {
                setLogin(evenement.currentTarget.value);
              }}
            />
            <Champ
              libelle="Mot de passe"
              name="motDePasse"
              type="password"
              autoComplete="current-password"
              required
              value={motDePasse}
              onChange={(evenement) => {
                setMotDePasse(evenement.currentTarget.value);
              }}
            />
            {/* Pleine largeur et hauteur tactile : c'est le seul geste de
                l'écran, et il se fait aussi bien au pouce sur un téléphone
                d'entrée de gamme qu'à la souris au guichet. */}
            <Bouton
              type="submit"
              variante="principal"
              taille="tactile"
              enCours={enCours}
              className="w-full"
            >
              Se connecter
            </Bouton>
          </form>

          {/* Mot de passe oublié : le geste est délibérément renvoyé à
              l'administrateur. L'API sait enregistrer une demande, mais la remise
              du jeton à l'usager relève du module de notification (lot L6) —
              proposer ici un formulaire qui n'aboutit nulle part ferait attendre
              un courriel qui n'arriverait jamais. */}
          <div className="mt-8 border-t border-border pt-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
              Mot de passe oublié ou compte verrouillé
            </p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Après cinq tentatives infructueuses, le compte est verrouillé. Votre administrateur le
              réinitialise et vous remet un mot de passe provisoire, à changer dès la première
              connexion.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

/** Ce que la plateforme permet de faire, en trois lignes et sans jargon. */
const REPERES: readonly { readonly titre: string; readonly detail: string }[] = [
  {
    titre: 'Déposer et suivre',
    detail: 'Demandes d’autorisation, pièces justificatives, avis des administrations.',
  },
  {
    titre: 'Délivrer et contrôler',
    detail: 'Titres sécurisés vérifiables hors ligne, inspections, journaux de pêche.',
  },
  {
    titre: 'Payer les redevances',
    detail: 'Quittances, échéances et état de compte au même endroit.',
  },
];

function VoletInstitutionnel() {
  return (
    <aside className="flex flex-col justify-between bg-institutionnel px-6 py-8 text-white/85 lg:px-12 lg:py-14">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/70">
          République de Guinée
        </p>
        <h1 className="mt-3 text-4xl font-bold tracking-tight text-white lg:text-5xl">SYGAP</h1>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-white/85 lg:text-base">
          Système de Gestion et d’Aménagement des Pêcheries — Ministère de la Pêche et de l’Économie
          Maritime.
        </p>
      </div>

      {/* Masqués sous 1024 px : sur un téléphone, le formulaire doit rester
          au-dessus de la ligne de flottaison, et ces repères sont du contexte,
          pas une étape du parcours. */}
      <ul className="my-10 hidden max-w-md flex-col lg:flex">
        {REPERES.map((repere) => (
          <li
            key={repere.titre}
            className="border-t border-white/15 py-4 last:border-b last:border-white/15"
          >
            <p className="text-sm font-semibold text-white">{repere.titre}</p>
            <p className="mt-1 text-sm leading-relaxed text-white/75">{repere.detail}</p>
          </li>
        ))}
      </ul>

      <p className="hidden text-xs leading-relaxed text-white/70 lg:block">
        Les données personnelles traitées ici relèvent de la loi L/2016/037/AN. L’accès est
        journalisé.
      </p>
    </aside>
  );
}
